import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { getAnalyticsTrends, getAnalyticsSitePerformance, getAnalyticsMatchingAnalytics } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell
} from 'recharts';
import {
  TrendingUp, Factory, ArrowRightLeft, BarChart3, Target,
  Package, Activity, Layers, MapPin
} from 'lucide-react';

const COLORS = ['#F97316', '#3B82F6', '#10B981', '#8B5CF6', '#EC4899', '#EAB308', '#06B6D4', '#EF4444', '#14B8A6', '#6366F1'];

const fmtNum = (v) => v != null ? Number(v).toLocaleString('de-DE') : '0';
const fmtEUR = (v) => v != null ? `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}` : '0';

export const AnalyticsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('trends');
  const [months, setMonths] = useState('12');
  const [trends, setTrends] = useState(null);
  const [sitePerf, setSitePerf] = useState(null);
  const [matchAnalytics, setMatchAnalytics] = useState(null);
  const [loading, setLoading] = useState({ trends: true, sites: true, matching: true });

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    loadTrends();
    loadSitePerformance();
    loadMatchingAnalytics();
  }, [user]);

  useEffect(() => {
    loadTrends();
  }, [months]);

  const loadTrends = async () => {
    setLoading(prev => ({ ...prev, trends: true }));
    try {
      const res = await getAnalyticsTrends(parseInt(months));
      setTrends(res.data);
    } catch (err) { console.error('Failed to load trends', err); }
    finally { setLoading(prev => ({ ...prev, trends: false })); }
  };

  const loadSitePerformance = async () => {
    setLoading(prev => ({ ...prev, sites: true }));
    try {
      const res = await getAnalyticsSitePerformance();
      setSitePerf(res.data);
    } catch (err) { console.error('Failed to load site performance', err); }
    finally { setLoading(prev => ({ ...prev, sites: false })); }
  };

  const loadMatchingAnalytics = async () => {
    setLoading(prev => ({ ...prev, matching: true }));
    try {
      const res = await getAnalyticsMatchingAnalytics();
      setMatchAnalytics(res.data);
    } catch (err) { console.error('Failed to load matching analytics', err); }
    finally { setLoading(prev => ({ ...prev, matching: false })); }
  };

  if (!user) return null;

  const trendData = trends?.trends?.map(item => ({
    ...item,
    month_short: item.month.slice(5),
  })) || [];

  return (
    <div className="min-h-screen bg-background" data-testid="analytics-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3" data-testid="analytics-title">
              <BarChart3 className="h-7 w-7 text-orange-500" />
              {t('analytics.title')}
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              {t('analytics.subtitle')}
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 max-w-md" data-testid="analytics-tabs">
            <TabsTrigger value="trends" data-testid="tab-trends">
              <TrendingUp className="h-4 w-4 mr-1.5" /> {t('analytics.tabTrends')}
            </TabsTrigger>
            <TabsTrigger value="sites" data-testid="tab-sites">
              <Factory className="h-4 w-4 mr-1.5" /> {t('analytics.tabSites')}
            </TabsTrigger>
            <TabsTrigger value="matching" data-testid="tab-matching">
              <ArrowRightLeft className="h-4 w-4 mr-1.5" /> {t('analytics.tabMatching')}
            </TabsTrigger>
          </TabsList>

          {/* TRENDS TAB */}
          <TabsContent value="trends" className="space-y-6">
            <div className="flex items-center gap-3">
              <Select value={months} onValueChange={setMonths}>
                <SelectTrigger className="w-[160px]" data-testid="months-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">{t('analytics.last6Months')}</SelectItem>
                  <SelectItem value="12">{t('analytics.last12Months')}</SelectItem>
                  <SelectItem value="24">{t('analytics.last24Months')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading.trends ? (
              <div className="space-y-4">
                <Skeleton className="h-80 rounded-xl" />
                <Skeleton className="h-80 rounded-xl" />
              </div>
            ) : (
              <>
                <Card data-testid="inventory-trend-chart">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Package className="h-5 w-5 text-blue-500" />
                      {t('analytics.inventoryTrend')}
                    </CardTitle>
                    <CardDescription>{t('analytics.inventoryTrendDesc')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={trendData}>
                          <defs>
                            <linearGradient id="gradBlue" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="gradOrange" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#F97316" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#F97316" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                          <XAxis dataKey="month_short" tick={{ fontSize: 11 }} />
                          <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                          <Tooltip
                            contentStyle={{ borderRadius: 8, fontSize: 12 }}
                            formatter={(value, name) => {
                              if (name === t('analytics.articles')) return [fmtNum(value), name];
                              if (name === t('analytics.valueKEUR')) return [`${fmtEUR(value)} kEUR`, name];
                              return [value, name];
                            }}
                          />
                          <Legend />
                          <Area yAxisId="left" type="monotone" dataKey="inventory_count" stroke="#3B82F6" fill="url(#gradBlue)" name={t('analytics.articles')} />
                          <Area yAxisId="right" type="monotone" dataKey="inventory_value_kEUR" stroke="#F97316" fill="url(#gradOrange)" name={t('analytics.valueKEUR')} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card data-testid="transaction-trend-chart">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Activity className="h-5 w-5 text-emerald-500" />
                        {t('analytics.transactionVolume')}
                      </CardTitle>
                      <CardDescription>{t('analytics.transactionVolumeDesc')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={trendData}>
                            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                            <XAxis dataKey="month_short" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                            <Legend />
                            <Bar dataKey="transaction_count" fill="#10B981" name={t('analytics.transactions')} radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  <Card data-testid="request-trend-chart">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Target className="h-5 w-5 text-violet-500" />
                        {t('analytics.requestTrend')}
                      </CardTitle>
                      <CardDescription>{t('analytics.requestTrendDesc')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={trendData}>
                            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                            <XAxis dataKey="month_short" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                            <Legend />
                            <Line type="monotone" dataKey="request_count" stroke="#8B5CF6" strokeWidth={2} name={t('analytics.requests')} dot={{ r: 3 }} />
                            <Line type="monotone" dataKey="request_completed" stroke="#10B981" strokeWidth={2} name={t('analytics.completed')} dot={{ r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* SITES TAB */}
          <TabsContent value="sites" className="space-y-6">
            {loading.sites ? (
              <div className="space-y-4">
                <Skeleton className="h-80 rounded-xl" />
                <Skeleton className="h-64 rounded-xl" />
              </div>
            ) : sitePerf?.sites?.length > 0 ? (
              <>
                <Card data-testid="site-comparison-chart">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Factory className="h-5 w-5 text-orange-500" />
                      {t('analytics.siteComparison')}
                    </CardTitle>
                    <CardDescription>{t('analytics.siteComparisonDesc')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={sitePerf.sites.slice(0, 10)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis dataKey="site_name" type="category" width={120} tick={{ fontSize: 11 }} />
                          <Tooltip
                            contentStyle={{ borderRadius: 8, fontSize: 12 }}
                            formatter={(v) => [`${fmtEUR(v)} kEUR`, t('analytics.value')]}
                          />
                          <Bar dataKey="total_value_kEUR" fill="#F97316" name={t('analytics.valueKEUR')} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="redistribution-ranking">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <ArrowRightLeft className="h-5 w-5 text-emerald-500" />
                      {t('analytics.redistributionRanking')}
                    </CardTitle>
                    <CardDescription>{t('analytics.redistributionRankingDesc')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={sitePerf.sites.filter(s => s.redistributed_count > 0).sort((a, b) => b.redistributed_count - a.redistributed_count).slice(0, 10)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis dataKey="site_name" type="category" width={120} tick={{ fontSize: 11 }} />
                          <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                          <Legend />
                          <Bar dataKey="redistributed_count" fill="#10B981" name={t('analytics.redistributions')} radius={[0, 4, 4, 0]} />
                          <Bar dataKey="pending_requests" fill="#EAB308" name={t('analytics.openRequests')} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="site-detail-table">
                  <CardHeader>
                    <CardTitle className="text-base">{t('analytics.siteDetails')}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left p-3 font-medium">{t('analytics.site')}</th>
                            <th className="text-right p-3 font-medium">{t('analytics.articles')}</th>
                            <th className="text-right p-3 font-medium">{t('analytics.value')}</th>
                            <th className="text-right p-3 font-medium">{t('analytics.slowMoving')}</th>
                            <th className="text-right p-3 font-medium">{t('analytics.redistributed')}</th>
                            <th className="text-right p-3 font-medium">{t('analytics.demand')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sitePerf.sites.map((site, i) => (
                            <tr key={site.site_id} className="border-b hover:bg-muted/30" data-testid={`site-row-${i}`}>
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                                  <div>
                                    <p className="font-medium">{site.site_name}</p>
                                    <p className="text-xs text-muted-foreground">{site.city}{site.country ? `, ${site.country}` : ''}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="text-right p-3 font-mono">{fmtNum(site.total_skus)}</td>
                              <td className="text-right p-3 font-mono">{fmtEUR(site.total_value_kEUR)}</td>
                              <td className="text-right p-3">
                                {site.slow_moving > 0 ? (
                                  <Badge variant="outline" className="text-amber-600 border-amber-300">{site.slow_moving}</Badge>
                                ) : <span className="text-muted-foreground">0</span>}
                              </td>
                              <td className="text-right p-3">
                                {site.redistributed_count > 0 ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">{site.redistributed_count}</Badge>
                                ) : <span className="text-muted-foreground">0</span>}
                              </td>
                              <td className="text-right p-3 font-mono">{fmtNum(site.demand_positions)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card><CardContent className="py-16 text-center text-muted-foreground">{t('analytics.noSiteData')}</CardContent></Card>
            )}
          </TabsContent>

          {/* MATCHING TAB */}
          <TabsContent value="matching" className="space-y-6">
            {loading.matching ? (
              <div className="space-y-4">
                <Skeleton className="h-28 rounded-xl" />
                <Skeleton className="h-80 rounded-xl" />
              </div>
            ) : matchAnalytics ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="matching-summary-kpis">
                  <Card className="border-l-4 border-l-orange-500">
                    <CardContent className="p-4">
                      <p className="text-xs font-medium text-muted-foreground uppercase">{t('analytics.kpiMatches')}</p>
                      <p className="text-2xl font-bold mt-1" data-testid="kpi-total-match-count">{fmtNum(matchAnalytics.summary.total_matches)}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-emerald-500">
                    <CardContent className="p-4">
                      <p className="text-xs font-medium text-muted-foreground uppercase">{t('analytics.kpiMatchRate')}</p>
                      <p className="text-2xl font-bold mt-1" data-testid="kpi-match-rate-analytics">{matchAnalytics.summary.match_rate_pct}%</p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <p className="text-xs font-medium text-muted-foreground uppercase">{t('analytics.kpiMatchedValue')}</p>
                      <p className="text-2xl font-bold mt-1">{fmtEUR(matchAnalytics.summary.matched_value_kEUR)} kEUR</p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-violet-500">
                    <CardContent className="p-4">
                      <p className="text-xs font-medium text-muted-foreground uppercase">{t('analytics.kpiMaterials')}</p>
                      <p className="text-2xl font-bold mt-1">{fmtNum(matchAnalytics.summary.unique_matched_materials)}</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card data-testid="top-matched-materials">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Package className="h-5 w-5 text-orange-500" />
                        {t('analytics.topMatchedMaterials')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {matchAnalytics.top_materials.length > 0 ? (
                        <div className="h-72">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={matchAnalytics.top_materials.slice(0, 8)} layout="vertical">
                              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                              <XAxis type="number" tick={{ fontSize: 11 }} />
                              <YAxis
                                dataKey="name"
                                type="category"
                                width={130}
                                tick={{ fontSize: 10 }}
                                tickFormatter={(v) => v.length > 18 ? v.slice(0, 18) + '...' : v}
                              />
                              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                              <Bar dataKey="count" fill="#F97316" name={t('matching.matches')} radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <p className="text-center text-muted-foreground py-8">{t('analytics.noData')}</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card data-testid="category-match-distribution">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Layers className="h-5 w-5 text-violet-500" />
                        {t('analytics.categoryDistribution')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {matchAnalytics.category_matches.length > 0 ? (
                        <div className="h-72">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={matchAnalytics.category_matches.slice(0, 8)}
                                cx="50%"
                                cy="50%"
                                outerRadius={90}
                                dataKey="count"
                                nameKey="category"
                                label={({ category, percent }) => `${category?.slice(0, 12)}: ${(percent * 100).toFixed(0)}%`}
                                labelLine={false}
                              >
                                {matchAnalytics.category_matches.slice(0, 8).map((_, i) => (
                                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <p className="text-center text-muted-foreground py-8">{t('analytics.noData')}</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {matchAnalytics.site_pairs.length > 0 && (
                  <Card data-testid="site-pair-flows">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <ArrowRightLeft className="h-5 w-5 text-blue-500" />
                        {t('analytics.sitePairFlows')}
                      </CardTitle>
                      <CardDescription>{t('analytics.sitePairFlowsDesc')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {matchAnalytics.site_pairs.map((pair, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                            data-testid={`site-pair-${i}`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <Badge variant="outline" className="text-xs shrink-0 border-orange-300 text-orange-700">{pair.from_site}</Badge>
                                <ArrowRightLeft className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                <Badge variant="outline" className="text-xs shrink-0 border-blue-300 text-blue-700">{pair.to_site}</Badge>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 shrink-0 ml-4">
                              <span className="text-sm font-bold">{pair.count} {t('matching.matches')}</span>
                              <span className="text-xs text-muted-foreground">{fmtEUR(pair.value_kEUR)} kEUR</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card><CardContent className="py-16 text-center text-muted-foreground">{t('analytics.noMatchingData')}</CardContent></Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AnalyticsPage;
